// React
import React, { FC } from 'react';

// Components
import IFrame from 'Components/IFrame';
import Form from 'Components/Form';
import { Input } from 'Components/Inputs';

interface Props {
  title: string;
  name: string;
  id: string;
  overrideStyles?: {
    display?: string;
    width?: string;
    height?: string;
    border?: string;
    marginLeft?: string;
  };
  loginUrl: string;
  targetUrl: string;
  ssoProvider: string;
  encryptedClaims: string;
  submit: (ref: React.RefObject<HTMLFormElement>) => void;
  lockScrolling?: boolean;
}

/**
 * GoodDataDashboard
 *
 * Embeds a GoodData Dashboard into the app by using several key parameters.
 * Will use a hidden form post to authenticate the user with GoodData and
 * target the resulting redirect into the embedded iframe.
 *
 * @param {string} loginUrl The URL of the GoodData Instance's login page.
 * @param {string} targetUrl The constructed URL of the GoodData Project.
 * @param {string} ssoProvider The name of the SSO Provider (who owns the private keys)
 * @param {string} encryptedClaims A PGP key that has been signed by the GoodData Public
 * Key and SSO Provider's Private Key using a secret password.  Signed on a per-school basis.
 * @param {(ref: React.RefObject<HTMLFormElement>) => void} submit A function that will be used whenever the form needs to submit.
 */
const GoodDataDashboard: FC<Props> = (props: Props) => {
  const {
    title,
    name,
    id,
    overrideStyles,
    loginUrl,
    targetUrl,
    ssoProvider,
    encryptedClaims,
    submit,
    lockScrolling,
  } = props;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', height: '100vh' }}>
      <IFrame
        title={title}
        name={name}
        id={id}
        overrideStyles={overrideStyles}
        lockScrolling={lockScrolling}
      />
      <Form
        name={`${name} Helper Form`}
        target={name}
        method="post"
        action={loginUrl}
        readyToSubmit={submit}
      >
        <Input
          key="targetUrl"
          name="targetUrl"
          type="hidden"
          value={targetUrl}
          ariaLabel="Target URL"
        />
        <Input
          key="ssoProvider"
          name="ssoProvider"
          type="hidden"
          value={ssoProvider}
          ariaLabel="SSO Provider"
        />
        <Input
          key="encryptedClaims"
          name="encryptedClaims"
          type="hidden"
          value={encryptedClaims}
          ariaLabel="Encrypted Claims"
        />
      </Form>
    </div>
  );
};

GoodDataDashboard.defaultProps = {
  overrideStyles: { marginLeft: '0px' },
  lockScrolling: false,
};

export default GoodDataDashboard;
try {
    // @ts-ignore
    GoodDataDashboard.displayName = "GoodDataDashboard";
    // @ts-ignore
    GoodDataDashboard.__docgenInfo = { "description": "GoodDataDashboard\n\nEmbeds a GoodData Dashboard into the app by using several key parameters.\nWill use a hidden form post to authenticate the user with GoodData and\ntarget the resulting redirect into the embedded iframe.", "displayName": "GoodDataDashboard", "props": { "title": { "defaultValue": null, "description": "", "name": "title", "required": true, "type": { "name": "string" } }, "name": { "defaultValue": null, "description": "", "name": "name", "required": true, "type": { "name": "string" } }, "id": { "defaultValue": null, "description": "", "name": "id", "required": true, "type": { "name": "string" } }, "overrideStyles": { "defaultValue": { value: "{ marginLeft: '0px' }" }, "description": "", "name": "overrideStyles", "required": false, "type": { "name": "{ display?: string; width?: string; height?: string; border?: string; marginLeft?: string; }" } }, "loginUrl": { "defaultValue": null, "description": "", "name": "loginUrl", "required": true, "type": { "name": "string" } }, "targetUrl": { "defaultValue": null, "description": "", "name": "targetUrl", "required": true, "type": { "name": "string" } }, "ssoProvider": { "defaultValue": null, "description": "", "name": "ssoProvider", "required": true, "type": { "name": "string" } }, "encryptedClaims": { "defaultValue": null, "description": "", "name": "encryptedClaims", "required": true, "type": { "name": "string" } }, "submit": { "defaultValue": null, "description": "", "name": "submit", "required": true, "type": { "name": "(ref: any) => void" } }, "lockScrolling": { "defaultValue": { value: false }, "description": "", "name": "lockScrolling", "required": false, "type": { "name": "boolean" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/containers/GoodData/components/GoodDataDashboard/GoodDataDashboard.tsx#GoodDataDashboard"] = { docgenInfo: GoodDataDashboard.__docgenInfo, name: "GoodDataDashboard", path: "src/shared/containers/GoodData/components/GoodDataDashboard/GoodDataDashboard.tsx#GoodDataDashboard" };
}
catch (__react_docgen_typescript_loader_error) { }